var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LogLevel, Utils, P, PCSS, CustomElement, Watch, PropertyConverters } from './_default-imports';
import { Matrix2D, Angle, Numbers } from '@pacem/pacem-foundation';
import { RotateEvent, RotateEventArgsClass, RotateEventType } from '../../ui/rotate';
import { CustomElementUtils } from '../../utils-customelement';
import { CustomEventUtils } from '../../utils-customevent';
import { avoidHandler } from '../../utils';
import { PacemBehavior } from '../behavior';
//namespace Pacem.Components {
const GET_VAL = CustomElementUtils.getAttachedPropertyValue;
const SET_VAL = CustomElementUtils.setAttachedPropertyValue;
const DEL_VAL = CustomElementUtils.deleteAttachedPropertyValue;
const MOUSE_DOWN = 'pacem:rotate:origin';
const DELEGATE = 'pacem:rotate:delegate';
const OFFSET = 'pacem:rotate:offset';
const PIVOT = 'pacem:rotate:center';
const TWO_PI = 2 * Math.PI;
class RotateElementDelegate {
    constructor(_element, _rotator, _logFn) {
        this._element = _element;
        this._rotator = _rotator;
        this._logFn = _logFn;
        this._startTime = Date.now();
        this._moveHandler = (evt) => {
            evt.stopPropagation();
            const el = this._element, previousPosition = this._previousPosition, rotator = this._rotator, minRotation = rotator.minRotation, maxRotation = rotator.maxRotation, currentPosition = (evt instanceof MouseEvent ? { x: evt.pageX, y: evt.pageY } : { x: evt.touches[0].pageX, y: evt.touches[0].pageY }), pivot = this._pivot;
            var currentAngle = this._computeAngle(pivot, currentPosition, previousPosition) + this._previousAngle;
            var renderedAngle = this._constraintAngle(currentAngle, rotator.step);
            this._logFn(LogLevel.Log, `previousPosition: ${JSON.stringify(previousPosition)}, currentPosition: ${JSON.stringify(currentPosition)}`, `Rotating`);
            if (currentAngle > maxRotation) {
                currentAngle = maxRotation;
            }
            while (renderedAngle > maxRotation && rotator.step > 0) {
                this._logFn(LogLevel.Log, `angle: ${renderedAngle} constrained to max: ${maxRotation}`, `Rotating`);
                renderedAngle -= rotator.step;
            }
            if (currentAngle < minRotation) {
                currentAngle = minRotation;
            }
            while (renderedAngle < minRotation && rotator.step > 0) {
                this._logFn(LogLevel.Log, `angle: ${renderedAngle} constrained to min: ${minRotation}`, `Rotating`);
                renderedAngle += rotator.step;
            }
            this._logFn(LogLevel.Log, `angle: ${renderedAngle}, origin: ${JSON.stringify(this._originalPosition)}, current: ${JSON.stringify(currentPosition)}`, `Rotating`);
            if (renderedAngle != this._previousRenderedAngle) {
                let evtRot = new RotateEvent(RotateEventType.Rotate, RotateEventArgsClass.fromArgs({
                    element: this._element, origin: this._originalPosition, startTime: this._startTime, currentPosition: currentPosition, rotation: renderedAngle, center: this._pivot
                }), { cancelable: true }, evt);
                this._rotator.dispatchEvent(evtRot);
                if (!evtRot.defaultPrevented) {
                    const m0 = this._originalTransform;
                    const m = Matrix2D.rotate(m0, renderedAngle - this._originalAngle);
                    el.style.transform = `matrix(${m.a}, ${m.b}, ${m.c}, ${m.d}, ${m.e}, ${m.f})`;
                    SET_VAL(this._element, OFFSET, renderedAngle);
                }
                this._previousRenderedAngle = renderedAngle;
            }
            // step
            this._previousPosition = currentPosition;
            this._previousAngle = currentAngle;
        };
        this._endHandler = (e) => {
            e.stopPropagation();
            let evt = new RotateEvent(RotateEventType.End, RotateEventArgsClass.fromArgs({
                element: this._element, origin: this._originalPosition, startTime: this._startTime, currentPosition: this._previousPosition, rotation: this._previousAngle, center: this._pivot
            }), {}, e);
            // body
            this._rotator.dispatchEvent(evt);
            DEL_VAL(this._element, DELEGATE);
            window.removeEventListener('mouseup', this._endHandler, false);
            window.removeEventListener('touchend', this._endHandler, false);
            window.removeEventListener('mousemove', this._moveHandler, false);
            window.removeEventListener('touchmove', this._moveHandler, false);
        };
        window.addEventListener('mouseup', this._endHandler, false);
        window.addEventListener('touchend', this._endHandler, false);
        window.addEventListener('mousemove', this._moveHandler, false);
        window.addEventListener('touchmove', this._moveHandler, false);
        this._originalPosition = this._previousPosition = GET_VAL(_element, MOUSE_DOWN);
        this._originalAngle = this._previousAngle = this._previousRenderedAngle = GET_VAL(_element, OFFSET, 0);
        this._pivot = GET_VAL(_element, PIVOT);
        //
        this._originalTransform = Utils.Css.deserializeTransform(_element);
    }
    _computeAngle(pivot, start, current) {
        return Angle.angleBetween(pivot, start, current);
    }
    _constraintAngle(x, step) {
        if (step > 0) {
            return Numbers.round(x, step);
        }
        return x;
    }
}
let PacemRotateElement = class PacemRotateElement extends PacemBehavior {
    constructor() {
        super(...arguments);
        this._startHandler = (evt) => {
            if (this.disabled) {
                return;
            }
            const coords = CustomEventUtils.getEventCoordinates(evt);
            const el = evt.currentTarget, origin = coords.page;
            const bbox = Utils.offsetRect(el), w2 = bbox.width / 2.0, h2 = bbox.height / 2.0;
            var pivot = this.center;
            if (Utils.isNull(pivot)) {
                pivot = { x: bbox.x + w2, y: bbox.y + h2 };
                el.style.transformOrigin = '50% 50%';
            }
            else {
                const offX = bbox.x + w2 - pivot.x, offY = bbox.y + h2 - pivot.y;
                el.style.transformOrigin = `${-offX}px ${-offY}px`;
            }
            // start
            const initEvent = new RotateEvent(RotateEventType.Start, RotateEventArgsClass.fromArgs({
                currentPosition: origin, rotation: GET_VAL(el, OFFSET, 0), center: pivot,
                origin: origin, element: el, startTime: Date.now()
            }), { cancelable: true }, evt);
            this.dispatchEvent(initEvent);
            // canceled?
            if (initEvent.defaultPrevented) {
                return;
            }
            // stop evt propagation (may cause page reload on touch/mobile devices)
            avoidHandler(evt);
            SET_VAL(el, MOUSE_DOWN, origin);
            SET_VAL(el, PIVOT, pivot);
            SET_VAL(el, DELEGATE, new RotateElementDelegate(el, this, 
            /* logging */ (level, message, category) => this.log.apply(this, [level, message, category])));
        };
    }
    decorate(element) {
        const el = element;
        Utils.addClass(el, PCSS + '-rotatable');
        const options = { capture: false, passive: false };
        element.addEventListener('mousedown', this._startHandler, false);
        element.addEventListener('touchstart', this._startHandler, options);
    }
    undecorate(element) {
        const el = element;
        Utils.removeClass(el, PCSS + '-rotatable');
        const options = { capture: false, passive: false };
        element.removeEventListener('mousedown', this._startHandler, false);
        element.removeEventListener('touchstart', this._startHandler, options);
    }
};
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Number })
], PacemRotateElement.prototype, "step", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Number })
], PacemRotateElement.prototype, "minRotation", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Number })
], PacemRotateElement.prototype, "maxRotation", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Point })
], PacemRotateElement.prototype, "center", void 0);
PacemRotateElement = __decorate([
    CustomElement({ tagName: P + '-rotate' })
], PacemRotateElement);
export { PacemRotateElement };
//}
